import React, { useState } from "react";
import {
  Menu,
  MenuItem,
  Stack,
  Typography,
  Button,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTranslations, useSnackbar } from "hooks";
import { getTranslation } from "common";
import useAppservices from "hooks/useAppservices";
import { useGoogleLogin } from "@react-oauth/google";
import axios, { AxiosError } from "axios";
import { flownodesApi } from "api";
import { Task } from "model/activitiesModel";

type SupportedTypes =
  | "document"
  | "spreadsheets"
  | "presentation"
  | "drawing"
  | "folder";

interface GoogleDriveMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onOpenPicker: () => void;
  onOpenFolderPicker: () => void;
  onRefreshAttachments?: () => void;
  task?: Task;
  organizationId: number | null | undefined;
  // onCreateDocument: (type: SupportedTypes, token?: string | null) => Promise<void>;
}

const GoogleDriveMenu: React.FC<GoogleDriveMenuProps> = ({
  anchorEl,
  open,
  onClose,
  onOpenPicker,
  onOpenFolderPicker,
  onRefreshAttachments,
  task,
  organizationId,
  // onCreateDocument,
}) => {
  const translations = useTranslations();
  const { sendSnack } = useSnackbar();
  const { appservices, handleLinkAppservice } = useAppservices();
  const [menuPhase, setMenuPhase] = useState<"main" | "create">("main");
  const redoGoogleDocRef = React.useRef<SupportedTypes | null>(null);

  const handleOpenCreateMenu = () => setMenuPhase("create");
  const handleBackToMainMenu = () => setMenuPhase("main");

  const failureGoogle = (error: any) => {
    console.error("failure google", error);
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async (e: any) => {
      const data = await handleLinkAppservice(e, 1);
      const token = data?.token;
      if (redoGoogleDocRef.current && token) {
        await createAndUploadGoogleDoc(redoGoogleDocRef.current, token);
      }
      redoGoogleDocRef.current = null;
    },
    onError: failureGoogle,
    flow: "auth-code",
    enable_serial_consent: false,
    scope: "https://www.googleapis.com/auth/drive.file",
  });

  const getIdTypeFromMimeType = (mimeType: string) => {
    switch (mimeType) {
      case "application/vnd.google-apps.folder":
        return 21;
      case "application/vnd.google-apps.document":
        return 22;
      case "application/vnd.google-apps.spreadsheet":
        return 23;
      case "application/vnd.google-apps.presentation":
        return 24;
      case "application/vnd.google-apps.drawing":
        return 25;
      default:
        return 20;
    }
  };

  const createGoogleDoc = async (type: string, accessToken: string) => {
    try {
      let mimeType = "";
      switch (type) {
        case "folder":
          mimeType = "application/vnd.google-apps.folder";
          break;
        case "spreadsheets":
          mimeType = "application/vnd.google-apps.spreadsheet";
          break;
        case "presentation":
          mimeType = "application/vnd.google-apps.presentation";
          break;
        case "document":
          mimeType = "application/vnd.google-apps.document";
          break;
        case "drawing":
          mimeType = "application/vnd.google-apps.drawing";
          break;
        default:
          throw new Error(getTranslation(translations, "google.file.not.supported"));
      }
      try {
        const response = await axios.post(
          "https://content.googleapis.com/drive/v3/files?supportsTeamDrives=true&fields=webViewLink%2Cname&alt=json",
          {
            mimeType,
            name: task?.flownodename,
          },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );
        return { docData: response.data, mimeType };
      } catch (error: any) {
        const _error: AxiosError = { ...error };
        if (_error.response?.status && _error.response?.status === 401) {
          redoGoogleDocRef.current = type as SupportedTypes;
          googleLogin();
        }
      }
    } catch (error) {
      // ignore
    }
    return { docData: null, mimeType: null };
  };

  const createAndUploadGoogleDoc = async (type: SupportedTypes, access_token: string | null = null) => {
    if (!appservices[1]) {
      sendSnack({
        type: "error",
        message: getTranslation(translations, "google.auth.failed.for.doc"),
      });
      return;
    }
    try {
      const { docData, mimeType } = await createGoogleDoc(type, access_token || appservices[1]);
      if (!docData) {
        sendSnack({
          type: "error",
          message: getTranslation(translations, "google.doc.create.error"),
        });
        return;
      }
      const payload = {
        idorganization: organizationId,
        filename: docData.name,
        cloudlink: docData.webViewLink,
        idtype: getIdTypeFromMimeType(mimeType || ""),
        description: docData.name,
      };
      await flownodesApi.post(`${task?.idprocesstoken}/attachments`, payload);
      sendSnack({
        type: "success",
        message: getTranslation(translations, "generic.fileuploadedsuccessfully"),
      });
      onRefreshAttachments?.();
    } catch {
      sendSnack({
        type: "error",
        message: getTranslation(translations, "generic.erroroccured"),
      });
    }
  };

  const handleCreateDocument = async (type: SupportedTypes) => {
    if (!appservices[1]) {
      sendSnack({
        type: "error",
        message: getTranslation(translations, "google.auth.failed.for.doc"),
      });
      return;
    }
    await createAndUploadGoogleDoc(type);
  };

  const handleClose = () => {
    setMenuPhase("main");
    onClose();
  };

  return (
    <Menu
      id="google-drive-menu"
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      MenuListProps={{ "aria-labelledby": "google-drive-button" }}
    >
      {menuPhase === "main" ? (
        <div>
          <MenuItem onClick={onOpenPicker}>
            {getTranslation(translations, "pm.startprocess.button.upload.file.drive")}
          </MenuItem>
          <MenuItem onClick={onOpenFolderPicker}>
            {getTranslation(translations, "pm.startprocess.button.upload.folder.drive")}
          </MenuItem>
          <MenuItem onClick={handleOpenCreateMenu}>
            {getTranslation(translations, "google.create.and.upload.file")}
          </MenuItem>
        </div>
      ) : (
        <div>
          {appservices[1] ? (
            <React.Fragment>
              <MenuItem onClick={handleBackToMainMenu}>
                <ArrowBackIcon />
              </MenuItem>
              <MenuItem onClick={() => handleCreateDocument("document")}>
                {getTranslation(translations, "general.google.doc")}
              </MenuItem>
              <MenuItem onClick={() => handleCreateDocument("spreadsheets")}>
                {getTranslation(translations, "general.google.sheet")}
              </MenuItem>
              <MenuItem onClick={() => handleCreateDocument("presentation")}>
                {getTranslation(translations, "general.google.presentation")}
              </MenuItem>
              <MenuItem onClick={() => handleCreateDocument("drawing")}>
                {getTranslation(translations, "general.google.drawing")}
              </MenuItem>
              <MenuItem onClick={() => handleCreateDocument("folder")}>
                {getTranslation(translations, "general.google.folder")}
              </MenuItem>
            </React.Fragment>
          ) : (
            <Stack sx={{ p: 1, maxWidth: 250 }}>
              <Typography fontSize={13}>
                {getTranslation(translations, "link.flowenti.drive.account")}
              </Typography>
              <Button size="small" variant="contained" onClick={googleLogin}>
                {getTranslation(translations, "link.flowenti.drive.account.button")}
              </Button>
            </Stack>
          )}
        </div>
      )}
    </Menu>
  );
};

export default GoogleDriveMenu;
